import { useQueries } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { checkIfShouldSkipChat } from '@/components/Chat/helpers'
import { ChatInboxModel, ChatMemberModel, ChatSmsPackageModel, NullableType } from '@/models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import { getCustomInboxes, getCustomInboxesQueryKey } from './get-custom-inboxes'
import { getInboxes, getInboxesQueryKey } from './get-inboxes'

export interface UseGetInboxesWithCustomProps {
  buildingId: number
  chatCustom?: boolean
}

interface ChatUser {
  user: {
    id: number | string
    role: string
  }
}

interface ChatConversation {
  lastMessage?: string
  buildingId: number
  channelName: string
}

export const useGetInboxesWithCustom = ({
  buildingId,
  chatCustom = false,
}: UseGetInboxesWithCustomProps) => {
  const currentBuilding = useSelector(selectActiveBuilding)

  const results = useQueries({
    queries: [
      {
        enabled: !!buildingId,
        queryFn: () => getInboxes({ buildingId }),
        queryKey: getInboxesQueryKey({ buildingId }),
      },
      {
        enabled: !!buildingId && chatCustom,
        queryFn: () => getCustomInboxes({ buildingId }),
        queryKey: getCustomInboxesQueryKey({ buildingId }),
      },
    ],
  })

  const [inboxesResult, customInboxesResult] = results

  const refetch = useCallback(() => {
    results.forEach((result) => result.refetch())
  }, [])

  const outputData = useMemo(() => {
    const data: ChatInboxModel[] = []
    let isLoading = false
    let isFetching = false
    let isError = false
    const errors: Error[] = []
    let firstCustomInbox: NullableType<ChatInboxModel> = null
    let smsPackage: NullableType<ChatSmsPackageModel> = null

    results.forEach((result) => {
      if (result.isLoading) isLoading = true
      if (result.isFetching) isFetching = true
      if (result.isError) isError = true
      if (result.error) errors.push(result.error)
    })

    // NOTE: inboxes will come no matter which user role is accessing it
    // e.g. `PMT | FDC | MT` - will always be returned no matter if its `ShiftManager` user that
    // must have access only for `FDC` inbox
    if (inboxesResult.data) {
      data.push(...inboxesResult.data.inboxes)
      smsPackage = inboxesResult.data.smsPackage
    }

    // NOTE: custom inboxes will come with inboxes user has access only
    if (customInboxesResult.data) {
      firstCustomInbox = customInboxesResult.data.inbox
      data.push(...customInboxesResult.data.inboxes)
    }

    // NOTE: This map will store different combinations of inbox lookups by id and type of inbox
    const inboxMappings = new Map<string, ChatInboxModel>()
    data.forEach((inbox) => {
      // -> String representation of {id} to inbox
      inboxMappings.set(`${inbox.id}`, inbox)

      // -> String representation of `{type}_{id}` to inbox
      inboxMappings.set(`${inbox.type}_${inbox.id}`, inbox)

      // -> Edge case - {type} to inbox mapping for not `CUST` inboxes
      if (inbox.type !== 'CUST') {
        inboxMappings.set(inbox.type, inbox)
      }
    })

    return {
      data,
      errors,
      firstCustomInbox,
      inboxMappings,
      isError,
      isFetching,
      isLoading,
      refetch,
      smsPackage,
    }
  }, [inboxesResult.dataUpdatedAt, customInboxesResult.dataUpdatedAt, refetch])

  const getFilteredByRoleInboxes = useCallback(
    (currentUser: ChatUser) => {
      return outputData.data.filter((inbox) => {
        if (checkIfShouldSkipChat(inbox.type, currentUser.user.role)) return false
        return inbox.members.some((member) => member.id === currentUser.user.id)
      })
    },
    [outputData.data],
  )

  const checkIfCanSeeConversationByUserAndInboxType = useCallback(
    (
      conversation: ChatConversation,
      currentUser: ChatUser,
      inbox: ChatInboxModel | null | undefined,
    ) => {
      // If no messages in `conversation` -> skip
      if (!conversation.lastMessage?.trim()) return false

      // If `conversation` from other building -> skip
      if (currentBuilding?.id !== conversation.buildingId) return false

      // We need to understand from which inbox is the current `conversation` comes from
      // It can be done either by:
      // - `inbox` param when the user directly filters by inbox
      // - `inbox` param is not provided -> it means that the user is filtering by `ALL` inboxes. If so,
      // then we need to map the `conversation` to the inbox it belongs to and filter by `members` contains `currentUser` param
      // and ensure it also matches with `inbox.type` param if it's not `ALL` inbox type
      const conversationType = conversation.channelName
        .slice(conversation.channelName.indexOf('_') + 1)
        // For some reason, we have `MTRQ` conversation types which should be mapped to `MT`
        // Also, similar to `CUST` inboxes, `MTRQ` ones also have `MTRQ_{id}` within them
        .replace(/MTRQ(_\d+)?$/, 'MT')
      const foundInbox = inbox?.id ? inbox : outputData.inboxMappings.get(conversationType)

      const inboxType = inbox?.type ?? 'ALL'
      // NOTE: `CUST` inboxes have `CUST_{id}` within them, others -> don't
      const inboxLookupType = inboxType === 'CUST' ? `${inboxType}_${inbox?.id}` : inboxType
      const isInCorrectType = inboxType === 'ALL' ? true : conversationType === inboxLookupType

      const isInboxMember = foundInbox?.members?.some(
        (member: ChatMemberModel) => member.id === currentUser.user.id,
      )

      if (isInboxMember && isInCorrectType) return true

      // Otherwise -> skip
      return false
    },
    [outputData.data, outputData.inboxMappings, currentBuilding?.id],
  )

  return { ...outputData, checkIfCanSeeConversationByUserAndInboxType, getFilteredByRoleInboxes }
}
